/**
 * ModuleViewer
 * Full-area viewer for an integrated module page.
 * Renders a breadcrumb header with a back-to-settings button,
 * then delegates to the appropriate renderer based on sourceModule.
 */

import React from 'react';
import { type ModuleItem } from '../../modules/moduleRegistry';
import { EdgeStreamPageRenderer } from './EdgeStreamPageRenderer';
import { EdgeInteractPageRenderer } from './EdgeInteractPageRenderer';
import { useUIStore } from '@flow-studio/store';

interface ModuleViewerProps {
  item: ModuleItem;
  onBack: () => void;
}

export const ModuleViewer: React.FC<ModuleViewerProps> = ({ item, onBack }) => {
  const { theme } = useUIStore();
  const isDark = theme === 'dark';
  const accent = item.accentColor;

  return (
    <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>

      {/* ── Breadcrumb header ── */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        padding: '0 24px 0 28px',
        height: '48px',
        borderBottom: isDark ? '1px solid rgba(255,255,255,0.08)' : '1px solid #e5e7eb',
        background: isDark ? 'rgba(2,6,23,0.7)' : 'rgba(255,255,255,0.9)',
        backdropFilter: 'blur(8px)',
        flexShrink: 0,
      }}>
        {/* Back button */}
        <button
          onClick={onBack}
          style={{
            padding: '5px 12px',
            background: 'transparent',
            border: isDark ? '1px solid rgba(255,255,255,0.12)' : '1px solid #e5e7eb',
            borderRadius: '6px',
            color: isDark ? '#94a3b8' : '#64748b',
            cursor: 'pointer',
            fontSize: '13px',
            fontWeight: 500,
            display: 'flex',
            alignItems: 'center',
            gap: '5px',
            transition: 'all 0.15s ease',
            whiteSpace: 'nowrap',
          }}
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <polyline points="15 18 9 12 15 6" />
          </svg>
          Settings
        </button>

        {/* Separator */}
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={isDark ? '#334155' : '#d1d5db'} strokeWidth="2">
          <polyline points="9 18 15 12 9 6" />
        </svg>

        {/* Icon */}
        <span style={{ color: accent, display: 'flex', alignItems: 'center' }}>
          {item.icon}
        </span>

        {/* Title */}
        <span style={{ fontSize: '15px', fontWeight: 600, color: isDark ? '#f8fafc' : '#111827' }}>
          {item.label}
        </span>

        {/* Group badge */}
        <span style={{
          padding: '2px 8px',
          borderRadius: '9999px',
          fontSize: '11px',
          background: `${accent}15`,
          color: accent,
          border: `1px solid ${accent}35`,
        }}>
          {item.group}
        </span>

        {/* Source module badge (right-aligned) */}
        <span style={{
          marginLeft: 'auto',
          padding: '3px 10px',
          borderRadius: '9999px',
          fontSize: '11px',
          fontWeight: 500,
          background: item.sourceModule === 'edge-stream' ? 'rgba(58,140,69,0.12)' : 'rgba(58,140,69,0.12)',
          color: item.sourceModule === 'edge-stream' ? '#3A8C45' : '#3A8C45',
          border: `1px solid ${item.sourceModule === 'edge-stream' ? 'rgba(58,140,69,0.25)' : 'rgba(58,140,69,0.25)'}`,
        }}>
          {item.sourceModule === 'edge-stream' ? 'EdgeStream' : 'EdgeInteract Explorer'}
        </span>
      </div>

      {/* ── Content area ── */}
      <div className="dark" data-theme="dark" style={{ flex: 1, overflow: 'hidden', minHeight: 0, background: '#020617', paddingLeft: '28px' }}>
        {item.sourceModule === 'edge-stream' ? (
          <EdgeStreamPageRenderer item={item} />
        ) : (
          <EdgeInteractPageRenderer item={item} />
        )}
      </div>
    </div>
  );
};
